import { Command as CommandPrimitive } from "cmdk"
import { ChevronRight, XIcon } from "lucide-react"
import { useMemo, useState, useRef } from "react"
import { Command, CommandEmpty, CommandGroup, CommandItem, CommandList } from "~/ui/command"
import { Input } from "~/ui/input"
import { Popover, PopoverAnchor, PopoverContent } from "~/ui/popover"
import { Skeleton } from "~/ui/skeleton"
import { Button } from "./button"
import { UserAvatar } from "~/users/user-avatar"

type Props<T extends string> = {
  selectedValue: T
  onSelectedValueChange: (value: T) => void
  searchValue: string
  onSearchValueChange: (value: string) => void
  items: {
    value: T
    label: string
    user?: any
  }[]
  isLoading?: boolean
  emptyMessage?: string
  placeholder?: string
  onSubmit?: () => void
  onReset?: () => void
}

export function SearchAutoComplete<T extends string>({
  selectedValue,
  onSelectedValueChange,
  searchValue,
  onSearchValueChange,
  items,
  isLoading,
  emptyMessage = "No items.",
  placeholder = "Search...",
  onSubmit,
  onReset,
}: Props<T>) {
  const [open, setOpen] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  // Open popover when there are items, loading, or when search has been performed (to show empty message)
  const shouldOpenPopover = items.length > 0 || isLoading || (searchValue.length > 0 && !isLoading)

  const labels = useMemo(
    () =>
      items.reduce(
        (acc, item) => {
          acc[item.value] = item.label
          return acc
        },
        {} as Record<string, string>
      ),
    [items]
  )

  const reset = () => {
    onSelectedValueChange("" as T)
    onSearchValueChange("")
    onReset?.()
    // Force clear the input element directly since CommandPrimitive.Input
    // with asChild pattern doesn't always sync properly with the HTML input
    if (inputRef.current) {
      inputRef.current.value = ""
    }
    inputRef.current?.blur()
    setOpen(false)
  }

  const onInputBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    if (!e.relatedTarget?.hasAttribute("cmdk-list") && labels[selectedValue] !== searchValue) {
      reset()
    }
  }

  const onSelectItem = (inputValue: string) => {
    if (inputValue === selectedValue) {
      reset()
    } else {
      onSelectedValueChange(inputValue as T)
      onSearchValueChange(labels[inputValue] ?? "")
      setOpen(false)
    }
  }

  return (
    <div className="flex items-center">
      <Popover open={open && shouldOpenPopover} onOpenChange={setOpen}>
        <Command shouldFilter={false}>
          <PopoverAnchor asChild>
            <CommandPrimitive.Input
              asChild
              value={searchValue}
              onValueChange={onSearchValueChange}
              onKeyDown={(e) => {
                if (e.key === "Escape") {
                  setOpen(false)
                } else if (e.key === "Enter") {
                  if (onSubmit) {
                    e.preventDefault()
                    e.stopPropagation()
                    setOpen(true) // Open popover when submitting search
                    onSubmit()
                  }
                }
              }}
              onBlur={onInputBlur}
            >
              <div className="group relative">
                <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                  <svg
                    className="h-5 w-5 text-gray-400 group-focus-within:text-purple-500"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                    />
                  </svg>
                </div>
                {searchValue && searchValue.length > 0 && (
                  <div className="absolute right-0 top-0 flex h-full items-center pr-3">
                    <Button
                      variant="outline"
                      className="h-5 w-5 rounded-full bg-white drop-shadow"
                      size="icon"
                      onClick={reset}
                      onMouseDown={(e) => e.preventDefault()}
                      tabIndex={-1}
                    >
                      <XIcon className="h-3 w-3" />
                    </Button>
                  </div>
                )}
                <Input
                  ref={inputRef}
                  placeholder={placeholder}
                  className="block w-full rounded-md bg-search-bg py-2 pl-10 pr-3 text-sm leading-5 placeholder-gray-500 focus:border-purple-500 focus:bg-purple-50 focus:outline-none"
                />
              </div>
            </CommandPrimitive.Input>
          </PopoverAnchor>
          {!open && <CommandList aria-hidden="true" className="hidden" />}
          <PopoverContent
            asChild
            onOpenAutoFocus={(e) => e.preventDefault()}
            onInteractOutside={(e) => {
              if (e.target instanceof Element && e.target.hasAttribute("cmdk-input")) {
                e.preventDefault()
              }
            }}
            className="w-[--radix-popover-trigger-width] bg-white p-0"
          >
            <CommandList className="bg-white">
              {isLoading && (
                <CommandPrimitive.Loading>
                  <div className="p-1">
                    <Skeleton className="h-6 w-full" />
                  </div>
                </CommandPrimitive.Loading>
              )}
              {items.length > 0 && !isLoading ? (
                <CommandGroup>
                  {items.map((option) => (
                    <CommandItem
                      className="!bg-white"
                      key={option.value}
                      value={option.value}
                      onMouseDown={(e) => e.preventDefault()}
                      onSelect={onSelectItem}
                    >
                      <div className="flex w-full items-center justify-between bg-white p-2">
                        <div className="flex items-center justify-start gap-2">
                          <UserAvatar user={option.user} className="h-8 w-8" />
                          {option.label}
                        </div>
                        <ChevronRight className="h-4 w-4" />
                      </div>
                    </CommandItem>
                  ))}
                </CommandGroup>
              ) : null}
              {!isLoading ? <CommandEmpty>{emptyMessage ?? "No results."}</CommandEmpty> : null}
            </CommandList>
          </PopoverContent>
        </Command>
      </Popover>
    </div>
  )
}
